import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuddyLogo, LumiMascot } from "../components/Brand";
import { Button } from "../components/ui/button";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Camera, MessageCircle, Shield, GraduationCap, School, Tablet,
  Sparkles, BookOpen, Ear, Eye, Heart, Zap, CheckCircle2,
} from "lucide-react";

function Nav() {
  const { t } = useTranslation();
  return (
    <header className="hb-glass sticky top-0 z-30">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="landing-logo"><BuddyLogo size={40} /></Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
          <a href="#families" className="hover:text-slate-900" data-testid="landing-nav-families">{t("nav.families")}</a>
          <a href="#teachers" className="hover:text-slate-900" data-testid="landing-nav-teachers">{t("nav.teachers")}</a>
          <a href="#schools" className="hover:text-slate-900" data-testid="landing-nav-schools">{t("nav.schools")}</a>
          <a href="#tablets" className="hover:text-slate-900" data-testid="landing-nav-tablets">{t("nav.tabletMakers")}</a>
          <Link to="/accessibility" className="hover:text-slate-900" data-testid="landing-nav-accessibility">{t("nav.accessibility")}</Link>
          <Link to="/pricing" className="hover:text-slate-900" data-testid="landing-nav-pricing">{t("nav.pricing")}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/login"><Button variant="ghost" data-testid="landing-btn-login">{t("common.signIn")}</Button></Link>
          <Link to="/register"><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="landing-btn-getstarted">{t("common.getStarted")}</Button></Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 grid md:grid-cols-4 gap-6">
        <div>
          <BuddyLogo size={40} />
          <p className="mt-3 text-sm text-slate-400">Learn Smarter. Do Better.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-2">Product</h4>
          <ul className="space-y-1 text-sm">
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            <li><Link to="/accessibility" className="hover:text-white">Accessibility</Link></li>
            <li><Link to="/activation" className="hover:text-white">Tablet Activation</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-2">Legal</h4>
          <ul className="space-y-1 text-sm">
            <li><Link to="/legal/privacy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/legal/childrens-privacy" className="hover:text-white">Children's Privacy</Link></li>
            <li><Link to="/legal/terms" className="hover:text-white">Terms of Service</Link></li>
            <li><Link to="/legal/parent-consent" className="hover:text-white">Parent Consent</Link></li>
            <li><Link to="/legal/school-data" className="hover:text-white">School Data Agreement</Link></li>
            <li><Link to="/legal/ai-safety" className="hover:text-white">AI Safety Policy</Link></li>
            <li><Link to="/legal/academic-integrity" className="hover:text-white">Academic Integrity</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-2">Support</h4>
          <ul className="space-y-1 text-sm">
            <li><Link to="/legal/support" className="hover:text-white">Contact & Support</Link></li>
            <li><Link to="/legal/accessibility-statement" className="hover:text-white">Accessibility Statement</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
        © 2026 AI Homework Buddy. All rights reserved.
      </div>
    </footer>
  );
}

function Feature({ icon: Icon, title, children, color = "bg-blue-500" }) {
  return (
    <div className="hb-card p-6">
      <div className={`w-10 h-10 rounded-lg ${color} text-white flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-adult font-bold text-slate-900 mb-1 text-lg">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen student-bg">
      <Nav />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-8 sm:pt-16 pb-12">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-yellow-100 text-yellow-900 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> {t("landing.heroBadge")}
            </div>
            <h1 className="font-student font-bold text-slate-900 mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {t("landing.heroTitle1")}<br/>
              <span className="text-blue-600">{t("landing.heroTitle2")}</span>
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-xl leading-relaxed">
              {t("landing.heroBody")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-6 text-base" data-testid="hero-btn-get-started">
                  {t("common.startFree")}
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full px-6 py-6 text-base border-slate-300" data-testid="hero-btn-signin">
                  {t("landing.iHaveAccount")}
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> {t("landing.kidSafe")}</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("landing.noAds")}</span>
              <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {t("landing.loved")}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative bg-white rounded-[36px] p-8 border border-slate-200 hb-chunky-shadow">
              <div className="flex flex-col items-center">
                <LumiMascot size={220} className="lumi-bounce" />
                <div className="mt-2 text-center">
                  <div className="font-student font-bold text-2xl text-slate-900">{t("landing.hiImLumi")}</div>
                  <div className="text-sm text-slate-600 mt-1">{t("brand.lumi_tag")}</div>
                </div>
                <div className="mt-4 w-full space-y-2">
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 text-sm text-slate-800">
                    {t("landing.quote1")}
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-3 text-sm text-slate-800 ml-6">
                    {t("landing.quote2")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core principle */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-12">
        <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12 border border-slate-800">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-yellow-300 font-bold">{t("landing.principleEyebrow")}</div>
              <h2 className="font-adult font-bold text-3xl sm:text-4xl mt-2">
                {t("landing.principleTitle")}
              </h2>
            </div>
            <ul className="grid gap-3 text-slate-200">
              {[t("landing.principlePoint1"), t("landing.principlePoint2"), t("landing.principlePoint3"), t("landing.principlePoint4")].map((tx) => (
                <li key={tx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" /> <span>{tx}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="families" className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-8">
        <h2 className="font-adult font-bold text-3xl text-slate-900 mb-6">{t("landing.featuresTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={Camera} title={t("landing.featScanner")} color="bg-blue-500">{t("landing.featScannerBody")}</Feature>
          <Feature icon={MessageCircle} title={t("landing.featVoice")} color="bg-green-500">{t("landing.featVoiceBody")}</Feature>
          <Feature icon={BookOpen} title={t("landing.featReading")} color="bg-yellow-500">{t("landing.featReadingBody")}</Feature>
          <Feature icon={GraduationCap} title={t("landing.featMath")} color="bg-slate-800">{t("landing.featMathBody")}</Feature>
        </div>
      </section>

      {/* For Teachers */}
      <section id="teachers" className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="hb-card p-8">
            <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-3"><GraduationCap className="w-5 h-5" /></div>
            <h3 className="font-adult font-bold text-2xl text-slate-900">{t("landing.forTeachersTitle")}</h3>
            <p className="text-slate-600 mt-2">{t("landing.forTeachersBody")}</p>
          </div>
          <div className="hb-card p-8" id="schools">
            <div className="w-10 h-10 rounded-lg bg-green-600 text-white flex items-center justify-center mb-3"><School className="w-5 h-5" /></div>
            <h3 className="font-adult font-bold text-2xl text-slate-900">{t("landing.forSchoolsTitle")}</h3>
            <p className="text-slate-600 mt-2">{t("landing.forSchoolsBody")}</p>
          </div>
        </div>
      </section>

      {/* Tablet manufacturers */}
      <section id="tablets" className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 sm:p-12">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Tablet className="w-8 h-8 mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-adult font-bold text-3xl">{t("landing.forTabletsTitle")}</h3>
              <p className="mt-2 max-w-2xl text-blue-100">{t("landing.forTabletsBody")}</p>
              <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link to="/activation" className="w-full sm:w-auto"><Button className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 rounded-full px-6" data-testid="tablets-cta-activate">{t("landing.redeemActivation")}</Button></Link>
                <Link to="/register" className="w-full sm:w-auto"><Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 rounded-full px-6" data-testid="tablets-cta-partner">{t("landing.becomePartner")}</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility hero */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-16">
        <div className="hb-card p-8 sm:p-12">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <h3 className="font-adult font-bold text-3xl text-slate-900">{t("landing.builtForLearners")}</h3>
              <p className="text-slate-600 mt-2">{t("landing.builtForBody")}</p>
              <Link to="/accessibility" className="inline-block mt-3">
                <Button variant="outline" className="rounded-full" data-testid="landing-btn-accessibility">{t("landing.exploreAccess")}</Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50 rounded-2xl p-4 text-center"><Ear className="w-6 h-6 text-blue-700 mx-auto" /><div className="text-xs mt-1 text-slate-700">{t("nav.families")}</div></div>
              <div className="bg-green-50 rounded-2xl p-4 text-center"><Eye className="w-6 h-6 text-green-700 mx-auto" /><div className="text-xs mt-1 text-slate-700">{t("access.low_vision")}</div></div>
              <div className="bg-yellow-50 rounded-2xl p-4 text-center"><Zap className="w-6 h-6 text-yellow-700 mx-auto" /><div className="text-xs mt-1 text-slate-700">{t("access.dyslexia")}</div></div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
